import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Icon, ICONS } from './icons';
import WorkflowTile from './WorkflowTile';
import { useLocalization, type Language } from '../../config/localization';

// Size type for workflow tiles
type TileSize = 1 | 2 | 3; // 1 = small, 2 = medium, 3 = large

interface WorkflowSelectorSettings {
  tileSize: TileSize;
  showArchived: boolean;
}

const STORAGE_KEY = 'workflowSelector_settings';

// Get stored settings from localStorage
function getStoredSettings(): WorkflowSelectorSettings {
  if (typeof window === 'undefined') {
    return { tileSize: 2, showArchived: false };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        tileSize: parsed.tileSize || 2,
        showArchived: parsed.showArchived ?? false
      };
    }
  } catch (error) {
    console.warn('Failed to load workflow selector settings:', error);
  }

  return { tileSize: 2, showArchived: false };
}

// Store settings to localStorage
function storeSettings(settings: WorkflowSelectorSettings): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save workflow selector settings:', error);
  }
}

interface WorkflowTileConfig {
  displayType?: 'single_image' | 'image_pair' | 'single_video' | 'slideshow' | null;
  imageUrl?: string;
  imageUrl2?: string;
  videoUrl?: string;
  comparisonMode?: 'slider' | 'auto_switch';
  autoSwitchDelay?: number;
  slideshowImages?: string[];
  slideshowDelay?: number;
  slideshowTransition?: 'instant' | 'fade' | 'slide';
}

interface WorkflowTag {
  id: number;
  name_en: string;
  name_ru: string;
  display_order: number;
}

interface WorkflowData {
  id: string;
  workflow_id: number;
  title: string;
  category: string;
  description?: string;
  tile_config?: WorkflowTileConfig | null;
  tags?: WorkflowTag[];
  display_status?: 'active' | 'new' | 'archived';
}

interface WorkflowTileSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  workflows: WorkflowData[];
  selectedWorkflowId: string | null;
  onSelect: (workflowId: string) => void;
  language: Language;
  workflowUsageHistory?: string[];
}

export default function WorkflowTileSelector({
  isOpen,
  onClose,
  workflows,
  selectedWorkflowId,
  onSelect,
  language,
  workflowUsageHistory = []
}: WorkflowTileSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [favoriteWorkflowIds, setFavoriteWorkflowIds] = useState<Set<number>>(new Set());
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [settings, setSettings] = useState<WorkflowSelectorSettings>(getStoredSettings);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const { getToken } = useAuth();
  const t = useLocalization(language);

  // Handle settings changes
  const handleSettingsChange = (newSettings: Partial<WorkflowSelectorSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  // Get grid classes based on tile size
  const getGridClasses = () => {
    const sizeClasses = {
      1: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
      2: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3',
      3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-2'
    };
    return sizeClasses[settings.tileSize] || sizeClasses[1];
  };

  // Load favorites on mount
  useEffect(() => {
    if (!isOpen) return;

    const loadFavorites = async () => {
      setIsLoadingFavorites(true);
      try {
        const token = await getToken({ template: 'withmail' });
        if (!token) return;

        const response = await fetch('/api/user/favorites', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setFavoriteWorkflowIds(new Set(data.favorite_workflow_ids || []));
        }
      } catch (error) {
        console.error('Failed to load favorites:', error);
      } finally {
        setIsLoadingFavorites(false);
      }
    };

    loadFavorites();
  }, [isOpen, getToken]);

  // Extract all unique tags from workflows and add virtual favorites tag
  const allTags = useMemo(() => {
    const tagMap = new Map<number, WorkflowTag>();
    workflows.forEach(wf => {
      wf.tags?.forEach(tag => {
        if (!tagMap.has(tag.id)) {
          tagMap.set(tag.id, tag);
        }
      });
    });

    const tags = Array.from(tagMap.values()).sort((a, b) => {
      if (a.display_order !== b.display_order) {
        return a.display_order - b.display_order;
      }
      return a.name_en.localeCompare(b.name_en);
    });

    // Always add virtual favorites tag at the beginning
    tags.unshift({
      id: -1,
      name_en: t.workflow?.favorites || 'Favorites',
      name_ru: t.workflow?.favorites || 'Избранное',
      display_order: -1
    });

    // Add virtual "New" tag before Favorites
    const newCount = workflows.filter(wf => wf.display_status === 'new').length;
    if (newCount > 0) {
      tags.unshift({
        id: -2,
        name_en: t.workflow?.newBadge || 'New',
        name_ru: t.workflow?.newBadge || 'Новое',
        display_order: -2
      });
    }

    return tags;
  }, [workflows, favoriteWorkflowIds, t.workflow?.favorites, t.workflow?.newBadge]);

  // Reset selectedTagId if the selected virtual "New" tag no longer exists
  useEffect(() => {
    if (selectedTagId === -2 && !allTags.some(tag => tag.id === -2)) {
      setSelectedTagId(null);
    }
  }, [allTags, selectedTagId]);

  // Count workflows per tag (including virtual favorites and new tags)
  const tagWorkflowCounts = useMemo(() => {
    const counts = new Map<number, number>();
    const visibleWorkflows = settings.showArchived
      ? workflows
      : workflows.filter(wf => (wf.display_status || 'active') !== 'archived');

    visibleWorkflows.forEach(wf => {
      wf.tags?.forEach(tag => {
        counts.set(tag.id, (counts.get(tag.id) || 0) + 1);
      });
    });
    // Add count for favorites tag (filtered by archived visibility)
    const visibleFavoriteCount = visibleWorkflows.filter(wf => favoriteWorkflowIds.has(wf.workflow_id)).length;
    counts.set(-1, visibleFavoriteCount);
    // Add count for "New" virtual tag (use visibleWorkflows for consistency)
    counts.set(-2, visibleWorkflows.filter(wf => wf.display_status === 'new').length);
    return counts;
  }, [workflows, favoriteWorkflowIds, settings.showArchived]);

  const filteredWorkflows = useMemo(() => {
    let result = workflows;

    // Filter out archived workflows unless showArchived is enabled
    if (!settings.showArchived) {
      result = result.filter(wf => (wf.display_status || 'active') !== 'archived');
    }

    // Filter by tag if selected
    if (selectedTagId !== null) {
      if (selectedTagId === -2) {
        // "New" virtual tag filter
        result = result.filter(wf => wf.display_status === 'new');
      } else if (selectedTagId === -1) {
        // Favorites filter
        result = result.filter(wf => favoriteWorkflowIds.has(wf.workflow_id));
      } else {
        // Regular tag filter
        result = result.filter(wf =>
          wf.tags?.some(tag => tag.id === selectedTagId)
        );
      }
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(wf =>
        wf.title.toLowerCase().includes(lowerSearch) ||
        wf.description?.toLowerCase().includes(lowerSearch)
      );
    }

    return result;
  }, [workflows, searchTerm, selectedTagId, favoriteWorkflowIds, settings.showArchived]);

  // Sort workflows: display_status priority first, then usage history
  const sortedWorkflows = useMemo(() => {
    const result = [...filteredWorkflows];

    // Display status sort priority: new (0) > active (1) > archived (2)
    const statusPriority = (status: string | undefined): number => {
      switch (status) {
        case 'new': return 0;
        case 'archived': return 2;
        default: return 1; // 'active' or undefined
      }
    };

    // Create a map of workflow ID to position in history (lower = more recent)
    const historyPositionMap = new Map<string, number>();
    (workflowUsageHistory || []).forEach((id, index) => {
      historyPositionMap.set(id, index);
    });

    result.sort((a, b) => {
      // Primary sort: display_status priority
      const aPriority = statusPriority(a.display_status);
      const bPriority = statusPriority(b.display_status);
      if (aPriority !== bPriority) return aPriority - bPriority;

      // Secondary sort: usage history
      const aHistoryPos = Math.min(
        historyPositionMap.get(a.id) ?? Infinity,
        historyPositionMap.get(a.workflow_id.toString()) ?? Infinity
      );
      const bHistoryPos = Math.min(
        historyPositionMap.get(b.id) ?? Infinity,
        historyPositionMap.get(b.workflow_id.toString()) ?? Infinity
      );

      if (aHistoryPos !== Infinity && bHistoryPos !== Infinity) {
        return aHistoryPos - bHistoryPos;
      }
      if (aHistoryPos !== Infinity) return -1;
      if (bHistoryPos !== Infinity) return 1;

      // Tertiary: keep original order
      return 0;
    });

    return result;
  }, [filteredWorkflows, workflowUsageHistory]);

  // Handle favorite toggle with optimistic UI update
  const handleToggleFavorite = async (workflowId: number) => {
    const isFavorite = favoriteWorkflowIds.has(workflowId);

    // Optimistic UI update
    setFavoriteWorkflowIds(prev => {
      const next = new Set(prev);
      if (isFavorite) {
        next.delete(workflowId);
      } else {
        next.add(workflowId);
      }
      return next;
    });

    try {
      const token = await getToken({ template: 'withmail' });
      if (!token) {
        // Revert optimistic update
        setFavoriteWorkflowIds(prev => {
          const next = new Set(prev);
          if (isFavorite) {
            next.add(workflowId);
          } else {
            next.delete(workflowId);
          }
          return next;
        });
        return;
      }

      const response = await fetch('/api/user/favorites', {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ workflow_id: workflowId })
      });

      if (!response.ok) {
        // Revert optimistic update on error
        setFavoriteWorkflowIds(prev => {
          const next = new Set(prev);
          if (isFavorite) {
            next.add(workflowId);
          } else {
            next.delete(workflowId);
          }
          return next;
        });
        console.error('Failed to toggle favorite');
      }
    } catch (error) {
      // Revert optimistic update on error
      setFavoriteWorkflowIds(prev => {
        const next = new Set(prev);
        if (isFavorite) {
          next.add(workflowId);
        } else {
          next.delete(workflowId);
        }
        return next;
      });
      console.error('Error toggling favorite:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#1a1f3a] rounded-2xl border-2 border-purple-500/30 w-full max-w-[90vw] h-[90vh] overflow-hidden flex flex-col shadow-2xl shadow-purple-500/20"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-purple-500/20 flex items-center gap-4 flex-shrink-0">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t.workflow?.searchWorkflowsPlaceholder || 'Поиск по названию или описанию...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#0f1420] text-white px-4 py-2 pr-10 rounded-lg border border-purple-500/30 focus:border-purple-500 focus:outline-none transition-colors"
              autoFocus
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Settings Button */}
          <button
            onClick={() => setShowSettingsPanel(!showSettingsPanel)}
            className={`p-2 rounded-lg transition-colors ${
              showSettingsPanel
                ? 'bg-purple-600 text-white'
                : 'bg-[#0f1420] text-gray-400 hover:text-purple-400 border border-purple-500/30'
            }`}
            aria-label="Settings"
          >
            <Icon name={ICONS.SLIDERS} size={20} />
          </button>
        </div>

        {/* Settings Panel - Floating popup */}
        {showSettingsPanel && (
          <div className="absolute top-20 right-6 z-50 w-80">
            <div className="bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-lg rounded-xl border-2 border-purple-500/50 shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-700/40">
                <div className="flex items-center gap-2">
                  <Icon name={ICONS.SLIDERS} size={20} className="text-purple-400" />
                  <h3 className="text-lg font-bold text-white">
                    {language === 'en' ? 'Display Options' : 'Настройки отображения'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowSettingsPanel(false)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors"
                >
                  <Icon name={ICONS.X} size={16} />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-6">
                {/* Tile Size Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">
                    {language === 'en' ? 'Tile Size' : 'Размер плиток'}
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSettingsChange({ tileSize: 1 })}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        settings.tileSize === 1
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-gray-800/50 text-gray-300 hover:bg-purple-900/30 hover:text-purple-300 border border-gray-700/50'
                      }`}
                    >
                      {language === 'en' ? 'Small' : 'Маленький'}
                    </button>
                    <button
                      onClick={() => handleSettingsChange({ tileSize: 2 })}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        settings.tileSize === 2
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-gray-800/50 text-gray-300 hover:bg-purple-900/30 hover:text-purple-300 border border-gray-700/50'
                      }`}
                    >
                      {language === 'en' ? 'Medium' : 'Средний'}
                    </button>
                    <button
                      onClick={() => handleSettingsChange({ tileSize: 3 })}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        settings.tileSize === 3
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                          : 'bg-gray-800/50 text-gray-300 hover:bg-purple-900/30 hover:text-purple-300 border border-gray-700/50'
                      }`}
                    >
                      {language === 'en' ? 'Large' : 'Большой'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content with Tags Sidebar and Tile Grid */}
        <div className="flex-1 overflow-hidden flex">
          {/* Tags Sidebar */}
          {allTags.length > 0 && (
            <div className="w-64 border-r border-purple-500/20 overflow-y-auto flex-shrink-0 bg-[#0f1420]">
              <div className="p-4">
                <h3 className="text-sm font-semibold text-purple-300 mb-3">
                  {t.workflow?.filterByTag || 'Фильтр по тегам'}
                </h3>

                {/* All Workflows button */}
                <button
                  onClick={() => setSelectedTagId(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg mb-2 transition-colors ${
                    selectedTagId === null
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                      : 'text-gray-300 hover:bg-purple-900/30 hover:text-purple-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm">
                      {t.workflow?.allWorkflows || 'Все воркфлоу'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {settings.showArchived
                        ? workflows.length
                        : workflows.filter(wf => (wf.display_status || 'active') !== 'archived').length
                      }
                    </span>
                  </div>
                </button>

                {/* Tag buttons */}
                {allTags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => setSelectedTagId(tag.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg mb-2 transition-colors ${
                      selectedTagId === tag.id
                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                        : 'text-gray-300 hover:bg-purple-900/30 hover:text-purple-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm flex items-center gap-2">
                        {tag.id === -2 && (
                          <Icon
                            name={ICONS.FIRE}
                            size={16}
                          />
                        )}
                        {tag.id === -1 && (
                          <Icon
                            name={ICONS.STAR_FILLED}
                            size={16}
                            className={selectedTagId === tag.id ? 'text-yellow-300' : 'text-yellow-400'}
                          />
                        )}
                        {language === 'ru' ? tag.name_ru : tag.name_en}
                      </span>
                      <span className={`text-xs ${selectedTagId === tag.id ? 'text-purple-200' : 'text-gray-500'}`}>
                        {tagWorkflowCounts.get(tag.id) || 0}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tile Grid */}
          <div className="flex-1 overflow-y-auto p-6">
          {sortedWorkflows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Icon name={ICONS.IMAGE} size={64} className="text-purple-500/40 mb-4" />
              <p className="text-purple-300/70 text-lg">
                {selectedTagId !== null
                  ? (t.workflow?.noWorkflowsWithTag || 'Нет воркфлоу с этим тегом')
                  : searchTerm
                    ? (t.workflow?.noWorkflowsFound || 'Воркфлоу не найдены')
                    : (t.workflow?.noWorkflows || 'Нет доступных воркфлоу')
                }
              </p>
              {(searchTerm || selectedTagId !== null) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTagId(null);
                  }}
                  className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors shadow-lg shadow-purple-500/30"
                >
                  {language === 'en' ? 'Show all workflows' : 'Показать все воркфлоу'}
                </button>
              )}
            </div>
          ) : (
            <div className={`grid ${getGridClasses()} gap-6`}>
              {sortedWorkflows.map((workflow) => (
                <WorkflowTile
                  key={workflow.id}
                  workflow={workflow}
                  isSelected={workflow.id === selectedWorkflowId}
                  onSelect={() => {
                    onSelect(workflow.id);
                    onClose();
                  }}
                  language={language}
                  isFavorite={favoriteWorkflowIds.has(workflow.workflow_id)}
                  onToggleFavorite={handleToggleFavorite}
                  size={settings.tileSize === 1 ? 'small' : settings.tileSize === 2 ? 'medium' : 'large'}
                  showTags={true}
                />
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Footer with show archived checkbox and count */}
        <div className="p-3 border-t border-purple-500/20 text-sm text-purple-300 flex-shrink-0 bg-[#0f1420] flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={settings.showArchived}
              onChange={(e) => handleSettingsChange({ showArchived: e.target.checked })}
              className="w-4 h-4 rounded border-purple-500/30 bg-[#1a1f3a] text-purple-500 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-gray-400 hover:text-purple-300 transition-colors">
              {t.workflow?.showArchived || (language === 'en' ? 'Show archived' : 'Показать архивные')}
            </span>
          </label>
          <span className="text-purple-300">
            {(() => {
              const totalVisible = settings.showArchived
                ? workflows.length
                : workflows.filter(wf => (wf.display_status || 'active') !== 'archived').length;
              return sortedWorkflows.length === totalVisible
                ? language === 'en'
                  ? `${totalVisible} workflow${totalVisible === 1 ? '' : 's'}`
                  : `${totalVisible} воркфлоу`
                : language === 'en'
                  ? `${sortedWorkflows.length} of ${totalVisible} workflows`
                  : `${sortedWorkflows.length} из ${totalVisible} воркфлоу`;
            })()}
          </span>
        </div>
      </div>
    </div>
  );
}
